/**
 * Clamp QC inspection serial format — same label rules as configuration and QC incidents:
 * 10 digits starting with 20, or US for domestic clamps.
 */

/** Matches configuration clamp serial and QC incident clamp hints. */
export const CLAMP_QC_INSPECTION_SERIAL_PATTERN = /^(20\d{8}|[Uu][Ss])$/;

export const CLAMP_QC_INSPECTION_SERIAL_TOO_LONG_MESSAGE =
  "Serial number is too long. Possible misscan.";

export const CLAMP_QC_INSPECTION_SERIAL_FORMAT_MESSAGE =
  "Clamp serial must be 10 digits starting with 20, or US for domestic clamps.";

export type ClampQcInspectionSerialFormatOptions = {
  /**
   * When true (Vite DEV), allow short non-clamp tokens for scanner sandboxing.
   * Still rejects empty, doubled, and over-length values.
   */
  allowNonNumeric?: boolean;
};

export type ClampQcInspectionSerialFormatResult =
  | { ok: true; serial: string }
  | { ok: false; error: string };

/** True when the value looks like a scanner double-read of the same label. */
export function clampQcInspectionSerialLooksLikeDoubledMisscan(trimmedSerial: string): boolean {
  const length = trimmedSerial.length;
  if (length < 4 || length % 2 !== 0) {
    return false;
  }
  const half = length / 2;
  return trimmedSerial.slice(0, half) === trimmedSerial.slice(half);
}

/**
 * Validates operator-entered or scanned clamp QC serial text before opening inspection or persisting.
 */
export function validateClampQcInspectionSerialFormat(
  rawSerial: string,
  options: ClampQcInspectionSerialFormatOptions = {},
): ClampQcInspectionSerialFormatResult {
  const serial = rawSerial.trim();
  if (serial === "") {
    return { ok: false, error: "Serial is required." };
  }

  if (CLAMP_QC_INSPECTION_SERIAL_PATTERN.test(serial)) {
    return { ok: true, serial };
  }

  if (serial.length > 10 || clampQcInspectionSerialLooksLikeDoubledMisscan(serial)) {
    return { ok: false, error: CLAMP_QC_INSPECTION_SERIAL_TOO_LONG_MESSAGE };
  }

  if (options.allowNonNumeric === true) {
    return { ok: true, serial };
  }

  return { ok: false, error: CLAMP_QC_INSPECTION_SERIAL_FORMAT_MESSAGE };
}
